import React, { useContext, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';
import queryString from 'query-string';

import { FEATURE_FLAGS } from 'constants/featureFlag';
import { FEATURED_SECTIONS } from 'constants/products';

import FeatureFlagContext from 'contexts/FeatureFlagContext';
import ProfileContext from 'contexts/ProfileContext';

import useInfiniteQuerySearchProducts from 'hooks/useInfiniteQuerySearchProducts';
import useInfiniteQueryFeaturedSectionProducts from 'hooks/useInfiniteQueryFeaturedSectionProducts';
import useQueryRelatedSearchProducts from 'hooks/useQueryRelatedSearchProducts';

import ProductSearchResults from './ProductSearchResults';

const ProductSearchResultsPage = () => {
  const feature_flag = useContext(FeatureFlagContext);
  const search_on_enter_vs_keystroke = feature_flag.flag(
    FEATURE_FLAGS.SEARCH_ON_ENTER_VS_KEYSTROKE
  );
  const history = useHistory();
  useEffect(() => {
    if (!feature_flag.flag(FEATURE_FLAGS.INITIAL_SEARCH_LAUNCH)) {
      history.replace('/explore');
    }
  }, [feature_flag, history]);

  const location = useLocation();
  const { employee, company } = useContext(ProfileContext);
  const handleError = useErrorHandler();

  const {
    q: query = '',
  }: {
    q?: string;
  } = queryString.parse(location.search);

  const {
    employee_wallets,
    settings: {
      employee_country_exchage_rate: employee_country_exchange_rate,
      country,
    },
  } = employee;

  const {
    data,
    is_loading: is_vendors_loading,
    fetchNextPage,
    has_next_page = false,
  } = useInfiniteQuerySearchProducts({
    a_b_test_id: search_on_enter_vs_keystroke,
    country,
    employee_wallets,
    onError: (error: Error) => handleError(error),
    query,
  });

  const product_pages = data?.pages;
  const products = useMemo(
    () => product_pages?.flatMap(({ products }) => products) ?? [],
    [product_pages]
  );
  const { total_products } = product_pages?.[0] ?? { total_products: 0 };

  const finished_paginating_results = total_products > 0 && !has_next_page;
  const { data: related_products } = useQueryRelatedSearchProducts({
    country,
    employee_wallets,
    onError: (error: Error) => handleError(error),
    products,
    query,
    finished_paginating_results,
  });

  const { data: unpopular_products_data } =
    useInfiniteQueryFeaturedSectionProducts({
      country,
      employee_wallets,
      exchange_rate: employee_country_exchange_rate,
      featured_section: FEATURED_SECTIONS.UNPOPULAR,
      onError: (error: Error) => handleError(error),
    });
  const { pages = [] } = unpopular_products_data ?? {};
  const unpopular_products = pages
    .flatMap(({ products }) => products)
    .slice(0, 9);

  return (
    <ProductSearchResults
      company={company}
      products={products}
      employee_country_exchange_rate={employee_country_exchange_rate}
      total_products={total_products!}
      query={query}
      fetchNextPage={fetchNextPage}
      has_next_page={has_next_page}
      related_products={related_products}
      unpopular_products={unpopular_products}
      is_vendors_loading={is_vendors_loading}
    />
  );
};

export default ProductSearchResultsPage;
